/**
 * Attachment uploads and downloads.
 *
 * Not GraphQL, and not a proxy either. The BFF hands out presigned URLs and
 * the browser talks to object storage directly:
 *
 *   1. POST /files/initiate   -> { uploadUrl, requiredHeaders, attachment }
 *   2. PUT  uploadUrl         -> the bytes, straight to storage
 *   3. POST /files/complete   -> the attachment becomes visible
 *
 * Step 2 is a plain XMLHttpRequest rather than fetch, for one reason: fetch
 * still cannot report upload progress. A sales rep on a congress-hall
 * connection uploading a 5 MB photo needs a progress bar, and `xhr.upload`
 * is the only browser API that provides one.
 *
 * If the user abandons the flow between 1 and 3, the reservation stays PENDING
 * and invisible; `discardUpload` cleans it up when they remove the file
 * deliberately, and the service's cleanup job catches the rest.
 */
import { getAccessToken } from './auth.jsx';
import { BFF_ORIGIN } from './runtimeEnv.js';

const BASE = BFF_ORIGIN;

/** An upload/download failure carrying the same code GraphQL errors use. */
export class FileError extends Error {
  constructor(message, code) {
    super(message);
    this.name = 'FileError';
    this.code = code ?? 'INTERNAL_SERVER_ERROR';
  }
}

async function callBff(path, { method = 'GET', body } = {}) {
  const token = getAccessToken();
  const response = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      ...(body ? { 'content-type': 'application/json' } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new FileError(
      payload?.error?.message ?? `request failed (${response.status})`,
      payload?.error?.code,
    );
  }
  return payload;
}

/** PUT the bytes to storage, reporting progress. Resolves when storage accepts. */
function putToStorage(url, headers, file, onProgress, signal) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [name, value] of Object.entries(headers ?? {})) {
      xhr.setRequestHeader(name, value);
    }

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    });
    xhr.addEventListener('load', () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new FileError(`storage rejected the upload (${xhr.status})`, 'UPLOAD_FAILED')),
    );
    xhr.addEventListener('error', () =>
      reject(new FileError('could not reach object storage', 'UPLOAD_FAILED')),
    );
    xhr.addEventListener('abort', () =>
      reject(new FileError('upload cancelled', 'UPLOAD_CANCELLED')),
    );

    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

/**
 * The whole flow. Resolves with the READY attachment.
 *
 * @param file        a File from an <input> or a drop
 * @param ownerType   INTERACTION | CONTACT | CONTACT_PHOTO
 * @param ownerId
 * @param onProgress  called with 0..1 during the transfer
 * @param signal      an AbortSignal to cancel the transfer
 */
export async function uploadFile({ file, ownerType, ownerId, onProgress, signal }) {
  const initiated = await callBff('/files/initiate', {
    method: 'POST',
    body: {
      ownerType,
      ownerId,
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
    },
  });

  try {
    await putToStorage(initiated.uploadUrl, initiated.requiredHeaders, file, onProgress, signal);
  } catch (err) {
    // The reservation is useless now; drop it so it does not sit in the
    // cleanup job's report. Best effort — the row is invisible either way.
    await discardUpload(initiated.attachment.id).catch(() => {});
    throw err;
  }

  const completed = await callBff('/files/complete', {
    method: 'POST',
    body: { attachmentId: initiated.attachment.id },
  });
  return completed.attachment;
}

/** Drop a reservation the user cancelled. PENDING only — the server enforces it. */
export function discardUpload(attachmentId) {
  return callBff('/files/discard', { method: 'POST', body: { attachmentId } });
}

/** A short-lived presigned GET for an attachment. */
export async function getDownloadUrl(attachmentId) {
  const response = await callBff(`/files/${attachmentId}/url`);
  return response.url;
}

/**
 * Open an attachment in a new tab.
 *
 * The URL is minted per click rather than held in state: it expires in minutes,
 * so a link rendered when the page loaded would be dead by the time anyone
 * clicked it.
 */
export async function openAttachment(attachmentId) {
  const url = await getDownloadUrl(attachmentId);
  window.open(url, '_blank', 'noopener,noreferrer');
}
